/**
 * Radial plot for the editor preview — pie and donut.
 *
 * Mirrors Chart\RadialRenderer. Same class names, same custom properties, same
 * geometry, so one stylesheet dresses both.
 */

import { pieSlices } from './radial-geometry';
import { formatShare, formatValue } from './format';
import { contrastInk } from './palettes';

/** Below this share a slice is too thin to hold its own label legibly. */
const LABEL_MIN_SHARE = 0.06;

export default function RadialPlot( { config } ) {
	const series = config.series[ 0 ];
	const { slices, total } = pieSlices( series.data, config.donutRatio );

	if ( ! slices.length ) {
		return null;
	}

	const colors = series.sliceColors || [];
	const surface = config.surface || 'var(--chart-surface)';

	return (
		<div className="brandy-chart__radial">
			<svg
				className="brandy-chart__pie"
				viewBox="0 0 100 100"
				aria-hidden="true"
				focusable="false"
			>
				{ slices.map( ( slice ) => (
					<path
						key={ slice.index }
						className="brandy-chart__slice"
						d={ slice.d }
						fill={ colors[ slice.index ] }
						// The separation between slices is a surface-coloured
						// stroke, not a gap in viewBox units: at 2px with
						// non-scaling-stroke it is exactly 2 device pixels of
						// surface at every size.
						stroke={ surface }
						strokeWidth="2"
						strokeLinejoin="round"
						vectorEffect="non-scaling-stroke"
					/>
				) ) }
			</svg>
			{ slices.map( ( slice ) => (
				<SliceLabel
					key={ slice.index }
					config={ config }
					slice={ slice }
					color={ colors[ slice.index ] }
				/>
			) ) }
			<Center config={ config } total={ total } />
		</div>
	);
}

function SliceLabel( { config, slice, color } ) {
	// A label wider than its wedge would be clipped or would overlap its
	// neighbour, so thin slices hand their value to the legend and tooltip.
	const showLabel =
		config.valueLabels !== 'none' &&
		( config.valueLabels === 'all' || slice.share >= LABEL_MIN_SHARE );

	if ( ! showLabel && ! config.tooltip ) {
		return null;
	}

	return (
		<span
			className="brandy-chart__slice-label"
			style={ {
				'--label-x': `${ slice.labelX }%`,
				'--label-y': `${ slice.labelY }%`,
			} }
			tabIndex={ config.tooltip ? 0 : undefined }
		>
			{ showLabel && (
				<span
					className="brandy-chart__slice-value"
					style={ { '--label-ink': contrastInk( color ) } }
				>
					{ formatShare( slice.share ) }
				</span>
			) }
			{ config.tooltip && (
				<span className="brandy-chart__tooltip" role="tooltip">
					<span className="brandy-chart__tooltip-title">
						{ config.categories[ slice.index ] ?? '' }
					</span>
					<span className="brandy-chart__tooltip-row">
						<span
							className="brandy-chart__tooltip-key"
							style={ { '--key-color': color } }
						/>
						<span className="brandy-chart__tooltip-value">
							{ formatValue( slice.value, config.format ) }
						</span>
						<span className="brandy-chart__tooltip-name">
							{ formatShare( slice.share ) }
						</span>
					</span>
				</span>
			) }
		</span>
	);
}

/**
 * The total in the middle of a donut — the hole is the one place a chart gets a
 * headline number for free.
 */
function Center( { config, total } ) {
	if ( config.type !== 'donut' || config.donutRatio < 0.35 ) {
		return null;
	}

	const caption = config.series[ 0 ].label;

	return (
		<span className="brandy-chart__center" aria-hidden="true">
			<span className="brandy-chart__center-value">
				{ formatValue( total, config.format ) }
			</span>
			{ caption && (
				<span className="brandy-chart__center-label">{ caption }</span>
			) }
		</span>
	);
}
